import React from "react"
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Typography,
} from "@mui/material"
import {Controller, useForm, FormProvider} from "react-hook-form"
import {zodResolver} from "@hookform/resolvers/zod"

import {useCustomerID} from "../../useCustomerID"

import {offerFormSchemaV2, OfferFormDataV2} from "./offerFormValidation"
import {PaymentOptionsSelector} from "./PaymentOptionsSelector"
import {OfferPeriodCard} from "./OfferPeriodCard"

import {colors} from "@indebted/theme/colors"
import {useCreateCustomerOffer} from "@indebted/api/customerOffersAPI"
import {useNotification} from "@indebted/components/Notification"
import {useAgent} from "@indebted/hooks/Agent"

type CreateOfferModalV2Props = {
	onClose: () => void
	onSuccess: () => void
	balanceInCents?: number
}

function CreateOfferModalV2({onClose, onSuccess, balanceInCents = 0}: CreateOfferModalV2Props) {
	const customerID = useCustomerID()
	const {notification} = useNotification()
	const [agentEmail] = useAgent()
	const [offerID] = React.useState(crypto.randomUUID())
	const {mutateAsync, isPending} = useCreateCustomerOffer()

	const methods = useForm<OfferFormDataV2>({
		resolver: zodResolver(offerFormSchemaV2),
		defaultValues: {
			startDate: "",
			endDate: "",
			notes: "",
			oneOff: undefined,
			paymentPlan: {
				tiers: [],
			},
		},
	})

	const {control, handleSubmit, trigger} = methods

	const onSubmit = async (data: OfferFormDataV2) => {
		try {
			const tiers: Array<
				| {tierId: string; tierType: "ONE_OFF"; discountRate: number}
				| {tierId: string; tierType: "PLAN"; discountRate: number; timeFrameWeeks: number}
			> = []

			if (data.oneOff?.discountRate !== undefined) {
				tiers.push({
					tierId: crypto.randomUUID(),
					tierType: "ONE_OFF",
					discountRate: data.oneOff.discountRate / 100,
				})
			}

			if (data.paymentPlan?.tiers) {
				data.paymentPlan.tiers.forEach((tier) => {
					tiers.push({
						tierId: tier.id,
						tierType: "PLAN",
						discountRate: tier.discountRate / 100,
						timeFrameWeeks: tier.maxTimeToPayWeeks,
					})
				})
			}

			await mutateAsync({
				customerID,
				offerId: offerID,
				startDate: data.startDate,
				endDate: data.endDate,
				tiers,
				agentEmail: agentEmail || undefined,
				notes: data.notes || undefined,
			})

			notification.success("Customer offer created successfully")
			onSuccess()
		} catch (error) {
			notification.error((error as {Message?: string}).Message || "Failed to create customer offer")
		}
	}

	const handleCreateOffer = async () => {
		const isValid = await trigger()
		if (isValid) {
			handleSubmit(onSubmit)()
		}
	}

	return (
		<Dialog open onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle sx={{pb: 0}}>
				<Typography sx={{fontSize: "1.25rem", fontWeight: 500, color: "rgba(0,0,0,0.87)", lineHeight: "2rem"}}>
					Create new offer
				</Typography>
				<Typography
					sx={{fontSize: "0.875rem", fontWeight: 400, color: colors.gray["500"], lineHeight: "1.25rem"}}
				>
					Set up a new payment offer with one or more payment options.
				</Typography>
			</DialogTitle>
			<FormProvider {...methods}>
				<form onSubmit={handleSubmit(onSubmit)}>
					<DialogContent>
						<OfferPeriodCard />

						<PaymentOptionsSelector balanceInCents={balanceInCents} />

						<Box mt={3}>
							<Typography variant="subtitle1" fontWeight="medium" gutterBottom>
								Internal notes
							</Typography>
							<Typography variant="body2" color="textSecondary" paragraph>
								For agent reference only (not visible to customer).
							</Typography>
							<Controller
								name="notes"
								control={control}
								render={({field}) => (
									<TextField
										{...field}
										placeholder="Add relevant info about this offer"
										multiline
										minRows={3}
										fullWidth
										size="small"
									/>
								)}
							/>
						</Box>
					</DialogContent>
					<DialogActions sx={{px: "1.5rem", pb: "1.5rem"}}>
						<Button
							onClick={onClose}
							disabled={isPending}
							variant="outlined"
							sx={{
								textTransform: "uppercase",
								borderColor: colors.warmBlue["900"],
								color: colors.warmBlue["900"],
								backgroundColor: colors.primary.white,
								"&:hover": {
									borderColor: colors.warmBlue["800"],
									backgroundColor: "rgba(71, 76, 222, 0.04)",
								},
							}}
						>
							Cancel
						</Button>
						<Button
							type="button"
							color="primary"
							variant="contained"
							disabled={isPending}
							onClick={handleCreateOffer}
						>
							{isPending ? <CircularProgress size={24} /> : "Create Offer"}
						</Button>
					</DialogActions>
				</form>
			</FormProvider>
		</Dialog>
	)
}

export {CreateOfferModalV2}
